import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { VirtualVehicleVehicle } from '@/@types/VirtualVehicle';
import useBranch from '../../../../data/branch/useBranch';
import useMarketCode from '../../../../data/branch/useMarketCode';
import useVirtualVehicles from '../../../../data/virtual-vehicles/useVirtualVehicles';
import InfoBox from '../../../../styled-components/components/InfoBox';
import InputSearch from '../../../../styled-components/components/InputSearch';
import { Link } from '../../../../styled-components/components/Link';
import { ShallowPanelBody } from '../../../../styled-components/components/Panel/ShallowPanel';
import { SeparatorList } from '../../../../styled-components/components/SeparatorList';
import { StackLarge } from '../../../../styled-components/components/Stack';
import { Text } from '../../../../styled-components/components/Text';
import { TemplateItem } from './TemplateItem';

interface AddFromTemplateProps {
  onSelect: (virtualVehicle: VirtualVehicleVehicle) => void;
  onClose: () => void;
  resetForm?: () => void;
}

const AddFromTemplate = ({ onSelect, onClose, resetForm }: AddFromTemplateProps) => {
  const [branch] = useBranch();
  const [search, setSearch] = useState('');

  const [marketCode] = useMarketCode();

  const { t } = useTranslation(['createVehiclePanel', 'common']);
  const [templates] = useVirtualVehicles(branch?.id ?? '');

  const getUniqueDisplayName = (template: VirtualVehicleVehicle) => {
    const modelWords = template.modelSeries.split(' ').filter((word) => word.length > 0);
    const salesWords = template.salesName.split(' ').filter((word) => word.length > 0);
    const uniqueWords = [...modelWords];
    const uniqueWordsLower = modelWords.map((word) => word.toLowerCase());

    salesWords.forEach((word) => {
      const wordLower = word.toLowerCase();
      if (!uniqueWordsLower.includes(wordLower)) {
        uniqueWords.push(word);
        uniqueWordsLower.push(wordLower);
      }
    });

    return uniqueWords.join(' ');
  };

  useEffect(() => {
    return () => {
      resetForm?.();
    };
  }, []);

  return (
    <ShallowPanelBody>
      <StackLarge>
        <InputSearch
          label={t('searchLabel', { ns: 'createVehiclePanel' })}
          id="search-vehicle"
          placeholder={t('searchPlaceholder', { ns: 'createVehiclePanel' })}
          onChange={(e) => setSearch(e.target.value)}
          value={search}
        />
      </StackLarge>
      <StackLarge>
        <SeparatorList $spacing={2}>
          {templates?.vehicles
            ?.slice()
            .sort((a, b) => {
              const nameA = `${a.manufacturer} ${getUniqueDisplayName(a)}`.toUpperCase();
              const nameB = `${b.manufacturer} ${getUniqueDisplayName(b)}`.toUpperCase();
              if (nameA < nameB) {
                return -1;
              }
              if (nameA > nameB) {
                return 1;
              }
              return 0;
            })
            .filter((template) => {
              const searchText =
                `${template.manufacturer} ${getUniqueDisplayName(template)}`.toUpperCase();
              return searchText.includes(search.toUpperCase());
            })
            .reduce((unique, template) => {
              const displayName = `${template.manufacturer} ${getUniqueDisplayName(template)}`;
              const isDuplicate = unique.some(
                (item) => `${item.manufacturer} ${getUniqueDisplayName(item)}` === displayName,
              );
              if (!isDuplicate) {
                unique.push(template);
              }
              return unique;
            }, [] as VirtualVehicleVehicle[])
            .map((template) => (
              <TemplateItem
                key={template.id}
                template={template}
                onSelect={() => {
                  onSelect(template);
                  onClose();
                }}
              />
            ))}
        </SeparatorList>
      </StackLarge>
      {marketCode === 'SE' && (
        <StackLarge>
          <InfoBox>
            <Text $size="small">{t('newTemplateFormLabel', { ns: 'createVehiclePanel' })}</Text>
            <Link
              href={'https://partner.wayke.se/ny-bilmall'}
              target="_blank"
              rel="noreferrer"
              $size="small"
            >
              {t('newTemplateFormLink', { ns: 'createVehiclePanel' })}
            </Link>
          </InfoBox>
        </StackLarge>
      )}
    </ShallowPanelBody>
  );
};

export default AddFromTemplate;
